import React from 'react';

import cookieStorage from 'helpers/cookieStorage';

const optionalIdSuffix = (id?: string) => (id ? `_${id}` : '');

const hiddenCookieName = (cookieBaseName: string, id?: string) =>
  `femtasy-app-${cookieBaseName}_HIDDEN${optionalIdSuffix(id)}`;

const countCookieName = (cookieBaseName: string, id?: string) =>
  `femtasy-app-${cookieBaseName}_SHOWN_COUNT${optionalIdSuffix(id)}`;

const getHiddenFromCookie = (cookieBaseName: string, id?: string) => {
  const hiddenCookie = hiddenCookieName(cookieBaseName, id);
  const countCookie = countCookieName(cookieBaseName, id);

  return (
    cookieStorage.getItem(countCookie) === '5' ||
    cookieStorage.getItem(hiddenCookie) === 'true'
  );
};

const setHiddenOnCookie = (
  cookieBaseName: string,
  hidden: boolean,
  cookieExpiration: number,
  shouldAdvanceCount: boolean,
  id?: string,
) => {
  const hiddenCookie = hiddenCookieName(cookieBaseName, id);
  const countCookie = countCookieName(cookieBaseName, id);

  cookieStorage.setItem(hiddenCookie, hidden, {
    expires: cookieExpiration,
  });

  if (shouldAdvanceCount) {
    const count = parseInt(cookieStorage.getItem(countCookie) || '0', 10);

    cookieStorage.setItem(countCookie, count + 1);
  }
};

const useHiddenWithCookie = (cookieBaseName: string, id?: string) => {
  const [hidden, setHiddenOnState] = React.useState<boolean>(
    getHiddenFromCookie(cookieBaseName, id),
  );

  // We actually want this to re-run on every render
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    const stored: boolean = getHiddenFromCookie(cookieBaseName, id);

    if (stored !== hidden) {
      // It was changed from a different component or tab
      setHiddenOnState(stored);
    }
  });

  const setHidden = (
    value: boolean,
    expirationInDays: number = 1,
    shouldAdvanceCount: boolean = true,
  ) => {
    setHiddenOnState(value);
    setHiddenOnCookie(
      cookieBaseName,
      value,
      expirationInDays,
      shouldAdvanceCount,
      id,
    );
  };

  const setPermanentlyHidden = () => {
    setHiddenOnState(true);
    setHiddenOnCookie(cookieBaseName, true, Infinity, true, id);
  };

  return [hidden, setHidden, setPermanentlyHidden] as const; // More details about "why" the const at https://kentcdodds.com/blog/wrapping-react-use-state-with-type-script#a-closer-look
};

export default useHiddenWithCookie;
